import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Input, Modal, Select, Spin, Tag, Upload, message } from 'antd';
import { CloseOutlined, InboxOutlined, PaperClipOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { friendlyError } from '../lib/api';
import { readVariantsProgress, runAiJob, variantKey, variantKeys, variantsStatusText, type VariantsProgress } from '../lib/ai-job';
import { wikiApi, editorApi, wikiAdminApi, aiApi, attachmentsApi, type AiArticleVariantDto, type ArticleFrontmatterInput } from '../lib/wikiApi';
import DOMPurify from 'dompurify';
import { markdownToHtml } from '../editor/markdown-html';
import { Chip } from './ds';
import { fmtSize } from './news/NewsAttachments';
import '../pages/KnowledgeBase.css';

const ROOT_VALUE = '__root__';
const NEW_PREFIX = '__new__:';

type ArticleType = NonNullable<ArticleFrontmatterInput['type']>;
type StartKind = 'template' | 'empty' | 'ai';
/** Типы материала = коды шаблонов (backend wiki-templates.ts). */
const TYPE_LABEL: Record<string, string> = {
  instruction: 'Инструкция', checklist: 'Чек-лист', regulation: 'Регламент', standard: 'Стандарт', article: 'Статья', faq: 'FAQ',
};
const POLL_MS = 3000;
/**
 * Страховка. Потерянную задачу (перезапуск сервера) ловит пульс на бэкенде и отвечает `code: 'lost'`,
 * поэтому здесь — только «совсем ничего не пришло».
 */
const GIVE_UP_MS = 30 * 60_000;
/** Файл больше этого ИИ не читает (бэкенд отдаёт 400) — он только прикладывается к статье. */
const AI_FILE_MAX_BYTES = 30 * 1024 * 1024;
const FILE_EXT = /\.(pdf|docx|txt)$/i;

const TYPE_OPTIONS: Array<{ value: ArticleType; label: string }> = [
  { value: 'instruction', label: 'Инструкция' },
  { value: 'checklist', label: 'Чек-лист' },
  { value: 'regulation', label: 'Регламент' },
  // Стандарт (16.10.2026): шаблон с «Ознакомлен» у точек; попадает в раздел «Стандарты».
  { value: 'standard', label: 'Стандарт' },
  { value: 'article', label: 'Статья' },
  { value: 'faq', label: 'FAQ' },
];

/**
 * Модалка «Новая статья» / «Создать статью» (HUB 2.0): заголовок, раздел,
 * подраздел (+ создать новый), тип и «с чего начать» — шаблон типа, пустая
 * страница или «ИИ: из текста» — вставили сколько угодно текста, ИИ
 * предлагает 2–4 варианта готовой статьи с подразделом (существующим или
 * новым), по выбранному создаём черновик (05.10.2026). Открывает инлайн-
 * редактор (`/article/<slug>?edit=1`).
 *
 * Если blockCode задан (создание со страницы раздела) — поле раздела скрыто.
 * Если не задан (кнопка «Создать статью» на лендинге БЗ) — показываем выбор
 * раздела (3 поля: раздел + подраздел + название).
 */
export function NewArticleModal({
  open,
  onClose,
  blockCode,
  defaultCategory,
  defaultType,
}: {
  open: boolean;
  onClose: () => void;
  /** Если задан — раздел фиксирован, поле выбора скрыто. */
  blockCode?: string;
  defaultCategory?: string;
  /** Тип материала при открытии («Создать стандарт» → standard). */
  defaultType?: ArticleType;
}): JSX.Element {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [block, setBlock] = useState<string | undefined>(blockCode);
  const [category, setCategory] = useState<string>(defaultCategory ?? ROOT_VALUE);
  // Текст в поиске подраздела — чтобы предложить «Создать подраздел «X»».
  const [catSearch, setCatSearch] = useState('');
  // HUB 2.0: тип материала (= код шаблона) и с чего начать: тело шаблона,
  // пустая страница или AI-черновик по тезисам.
  const [type, setType] = useState<ArticleType>(defaultType ?? 'instruction');
  const [start, setStart] = useState<StartKind>('template');
  const [points, setPoints] = useState('');
  // Файл (PDF/DOCX/TXT): ИИ читает его в режиме «из текста», а после создания
  // статьи он прикладывается к ней вложением.
  const [file, setFile] = useState<File | null>(null);
  // «ИИ: из текста»: сколько вариантов, ход фоновой задачи, результат.
  const [variantCount, setVariantCount] = useState(3);
  const [aiRunning, setAiRunning] = useState(false);
  const [aiElapsed, setAiElapsed] = useState(0);
  const [aiError, setAiError] = useState<string | null>(null);
  const [aiNote, setAiNote] = useState<string | null>(null);
  // Ход работы: пока ИИ пишет, готовые варианты уже лежат в `variants`, а `aiProgress` — для подписи и заглушек.
  const [aiProgress, setAiProgress] = useState<VariantsProgress | null>(null);
  const [aiNotice, setAiNotice] = useState<string | null>(null);
  const [variants, setVariants] = useState<AiArticleVariantDto[] | null>(null);
  // Какой вариант открыт: по ключу, а не по номеру — варианты приходят по мере готовности и сдвигают друг друга.
  const [previewKey, setPreviewKey] = useState<string | null>(null);
  const runId = useRef(0);

  // При открытии/смене входных пропсов — синхронизируем стейт.
  useEffect(() => {
    if (open) {
      setBlock(blockCode);
      setCategory(defaultCategory ?? ROOT_VALUE);
      setCatSearch('');
      setType(defaultType ?? 'instruction');
      setStart('template');
      setPoints('');
      setFile(null);
      setVariants(null);
      setAiError(null);
      setAiNote(null);
      setAiNotice(null);
      setAiProgress(null);
      setPreviewKey(null);
    } else {
      runId.current += 1;
      setAiRunning(false);
    }
  }, [open, blockCode, defaultCategory, defaultType]);

  const templatesQuery = useQuery({
    queryKey: ['wiki', 'templates'],
    queryFn: () => wikiApi.listTemplates(),
    enabled: open,
    staleTime: 10 * 60_000,
  });
  const typeTemplate = (templatesQuery.data ?? []).find((t) => t.code === type);

  // Список разделов (если выбор раздела нужен).
  const blocksQuery = useQuery({
    queryKey: ['wiki', 'blocks'],
    queryFn: () => wikiApi.listBlocks(),
    enabled: open && !blockCode,
    staleTime: 60_000,
  });

  // Подразделы выбранного раздела.
  const effectiveBlock = blockCode ?? block;
  const catsQuery = useQuery({
    queryKey: ['wiki', 'categories', effectiveBlock],
    queryFn: () => wikiApi.listCategories(effectiveBlock as string),
    enabled: open && !!effectiveBlock,
    staleTime: 30_000,
  });
  const existingNames = Array.from(new Set((catsQuery.data ?? []).map((c) => c.name))).sort((a, b) =>
    a.localeCompare(b, 'ru'),
  );
  const trimmedSearch = catSearch.trim();
  const searchMatchesExisting = existingNames.some(
    (n) => n.toLowerCase() === trimmedSearch.toLowerCase(),
  );
  const categoryOptions = [
    { value: ROOT_VALUE, label: 'Без подраздела (основные статьи)' },
    ...existingNames.map((name) => ({ value: name, label: name })),
    // Предложение создать новый подраздел прямо из этого поля.
    ...(trimmedSearch && !searchMatchesExisting
      ? [{ value: `${NEW_PREFIX}${trimmedSearch}`, label: `➕ Создать подраздел «${trimmedSearch}»` }]
      : []),
  ];

  // Заголовок по умолчанию — имя файла без расширения (в режиме ИИ заголовок предложит модель).
  const fileTitle = file ? file.name.replace(/\.[^.]+$/, '').replace(/[_\s]+/g, ' ').trim().slice(0, 200) : '';
  const effectiveTitle = title.trim() || fileTitle;
  // ИИ читает файл только до 30 МБ; больший просто прикладывается к статье.
  const aiFile = file && file.size <= AI_FILE_MAX_BYTES ? file : null;

  // Исходный файл — во «Вложения» новой статьи (не inline). Сбой не мешает открыть статью.
  const attachFile = async (articleId: string): Promise<void> => {
    if (!file) return;
    const key = 'new-article-attach';
    message.open({ key, type: 'loading', content: 'Прикрепляем файл…', duration: 0 });
    try {
      await attachmentsApi.uploadParallel(articleId, file, (p) => {
        message.open({ key, type: 'loading', content: `Прикрепляем файл… ${p}%`, duration: 0 });
      }, false);
      message.destroy(key);
    } catch (e) {
      message.destroy(key);
      message.error(`Статья создана, но файл не прикрепился: ${(e as Error).message}`, 6);
    }
  };

  // Другой файл — прежние варианты ИИ относились к старому: сбрасываем и останавливаем опрос.
  const changeFile = (f: File | null): void => {
    runId.current += 1;
    setAiRunning(false);
    setVariants(null);
    setPreviewKey(null);
    setAiError(null);
    setAiNote(null);
    setAiNotice(null);
    setAiProgress(null);
    setFile(f);
  };

  const createMut = useMutation({
    mutationFn: async () => {
      // Если выбран «создать новый подраздел» — сначала создаём категорию,
      // затем используем её имя в frontmatter статьи.
      let effectiveCategory = category === ROOT_VALUE ? undefined : category;
      if (category.startsWith(NEW_PREFIX) && effectiveBlock) {
        const name = category.slice(NEW_PREFIX.length);
        const created = await wikiAdminApi.createCategory(effectiveBlock, name);
        effectiveCategory = created?.name ?? name;
      }
      const res = await editorApi.create({
        title: effectiveTitle,
        bodyMd: '',
        frontmatter: {
          blocks: effectiveBlock ? [effectiveBlock] : undefined,
          category: effectiveCategory,
          type,
          // С файлом включаем «Скачать вложение» на странице статьи.
          downloadable: file ? true : undefined,
          status: 'DRAFT',
        },
        // Тело шаблона подставляет бэкенд, когда bodyMd пуст.
        templateCode: start === 'template' ? type : 'empty',
      });
      await attachFile(res.id);
      return res;
    },
    onSuccess: (res) => {
      onClose();
      setTitle('');
      navigate(`/article/${encodeURIComponent(res.slug)}?edit=1`);
    },
    onError: (e: Error) => message.error(e.message || 'Не удалось создать статью'),
  });

  // Черновик из выбранного варианта ИИ: новый подраздел создаём сначала.
  const fromVariant = useMutation({
    mutationFn: async (v: AiArticleVariantDto) => {
      let cat = v.category ?? undefined;
      if (v.categoryIsNew && v.category && effectiveBlock) {
        const created = await wikiAdminApi.createCategory(effectiveBlock, v.category);
        cat = created?.name ?? v.category;
      }
      const res = await editorApi.create({
        title: v.title,
        bodyMd: v.bodyMd,
        frontmatter: { blocks: effectiveBlock ? [effectiveBlock] : undefined, category: cat, type: v.type, excerpt: v.excerpt || undefined, downloadable: file ? true : undefined, status: 'DRAFT' },
        templateCode: 'empty',
      });
      await attachFile(res.id);
      return res;
    },
    onSuccess: (res) => {
      onClose();
      setTitle('');
      navigate(`/article/${encodeURIComponent(res.slug)}?edit=1`);
    },
    onError: (e: Error) => message.error(e.message || 'Не удалось создать статью'),
  });

  const runVariants = async (): Promise<void> => {
    if (!effectiveBlock) return;
    const my = ++runId.current;
    setAiRunning(true);
    setAiError(null);
    setAiNote(null);
    setAiNotice(null);
    setVariants(null);
    setPreviewKey(null);
    setAiProgress(null);
    setAiElapsed(0);
    try {
      const chosenCat = category === ROOT_VALUE ? undefined : category.startsWith(NEW_PREFIX) ? category.slice(NEW_PREFIX.length) : category;
      const startJob = async (): Promise<{ jobId: string }> => {
        if (aiFile) {
          // С файлом: multipart — ИИ читает документ и коротко описывает его.
          const fd = new FormData();
          fd.append('blockCode', effectiveBlock);
          if (points.trim()) fd.append('text', points.trim());
          if (title.trim()) fd.append('title', title.trim());
          if (chosenCat) fd.append('category', chosenCat);
          fd.append('type', type);
          fd.append('count', String(variantCount));
          fd.append('file', aiFile);
          return aiApi.startArticleVariantsFile(fd);
        }
        return aiApi.startArticleVariants({
          blockCode: effectiveBlock,
          text: points.trim(),
          title: title.trim() || undefined,
          category: chosenCat,
          type,
          count: variantCount,
        });
      };
      const out = await runAiJob({
        start: startJob,
        status: (jobId) => aiApi.articleVariantsStatus(jobId),
        alive: () => runId.current === my,
        pollMs: POLL_MS,
        giveUpMs: GIVE_UP_MS,
        giveUpText: 'ИИ не ответил за 30 минут — попробуйте ещё раз',
        onTick: ({ elapsedSec, progress }) => {
          setAiElapsed(elapsedSec);
          const p = readVariantsProgress(progress);
          setAiProgress(p);
          // Готовые варианты показываем сразу, пока остальные пишутся.
          if (p && p.variants.length > 0) setVariants(p.variants);
        },
        // Сервер перезапускался и потерял задачу — запускаем заново один раз; написанное прежней попыткой пропало.
        onRestart: () => {
          setAiNotice('Сервер обновлялся — запустили заново');
          setVariants(null);
          setPreviewKey(null);
          setAiProgress(null);
        },
      });
      if (out.kind === 'done') {
        setVariants(out.result.variants);
        setAiNote(out.result.note);
      } else if (out.kind === 'error') {
        // Готовое, что успели показать, оставляем: его уже можно взять в черновик.
        setAiError(out.error);
      }
    } catch (e) {
      if (runId.current === my) setAiError(friendlyError(e));
    } finally {
      if (runId.current === my) {
        setAiRunning(false);
        setAiProgress(null);
        setAiNotice(null);
      }
    }
  };

  const aiMode = start === 'ai';
  // Недописанные варианты — карточки-заглушки. Ход работы приходит с первого опроса, до него считаем по выбранному числу.
  const pendingCount = aiRunning ? Math.max(0, (aiProgress?.total ?? variantCount) - (aiProgress?.done ?? 0)) : 0;
  const readyCount = variants?.length ?? 0;
  const keys = variantKeys(variants ?? []);
  const previewVariant = previewKey != null ? (variants ?? [])[keys.indexOf(previewKey)] : undefined;
  const canCreate = aiMode
    ? !!effectiveBlock && (!!aiFile || points.trim().length >= 20) && !aiRunning
    : effectiveTitle.length >= 2 && !!effectiveBlock;
  const submit = (): void => {
    if (!canCreate) return;
    if (aiMode) void runVariants();
    else if (!createMut.isPending) createMut.mutate();
  };

  const startCards: Array<{ key: StartKind; title: string; desc: string }> = [
    { key: 'template', title: typeTemplate ? `Шаблон «${typeTemplate.name}»` : 'По шаблону', desc: typeTemplate?.description ?? 'Готовая структура разделов под тип' },
    { key: 'empty', title: 'Пустая', desc: 'Чистый лист — структура на ваше усмотрение' },
    { key: 'ai', title: 'ИИ: из текста', desc: 'Вставьте текст или загрузите файл (PDF, DOCX) — ИИ предложит 2–4 варианта статьи' },
  ];

  return (
    <Modal
      width={aiMode && (readyCount > 0 || pendingCount > 0) ? 920 : 640}
      centered
      title={defaultType === 'standard' ? 'Новый стандарт' : blockCode ? 'Новая статья' : 'Создать статью'}
      open={open}
      onCancel={() => {
        onClose();
        setTitle('');
      }}
      onOk={submit}
      okText={aiMode ? (aiRunning ? 'ИИ готовит варианты…' : variants ? 'Предложить заново' : 'Предложить варианты') : 'Создать и открыть редактор'}
      cancelText="Отмена"
      okButtonProps={{ disabled: !canCreate, loading: aiMode ? aiRunning : createMut.isPending }}
      destroyOnHidden
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 6 }}>
        <div>
          <label className="fk-kb-label">{aiMode ? 'Заголовок (необязательно — ИИ предложит)' : 'Заголовок'}</label>
          <Input
            size="large"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={file && !aiMode ? 'Пусто — возьмём имя файла' : 'Коротко и по делу: «Срок хранения соуса унаги»'}
            autoFocus
            maxLength={200}
            onPressEnter={submit}
            style={{ fontWeight: 600 }}
          />
        </div>
        <div className="fk-kb-newart-grid">
          {!blockCode && (
            <div>
              <label className="fk-kb-label">Раздел</label>
              <Select
                style={{ width: '100%' }}
                value={block}
                onChange={(v) => {
                  setBlock(v);
                  setCategory(ROOT_VALUE);
                }}
                placeholder="Выберите раздел"
                options={(blocksQuery.data ?? []).map((b) => ({ value: b.code, label: b.name }))}
                showSearch
                optionFilterProp="label"
                loading={blocksQuery.isLoading}
              />
            </div>
          )}
          <div>
            <label className="fk-kb-label">Подраздел</label>
            <Select
              style={{ width: '100%' }}
              value={category}
              onChange={(v) => {
                setCategory(v);
                setCatSearch('');
              }}
              options={categoryOptions}
              showSearch
              // searchValue управляется вручную, чтобы предложить «Создать подраздел «X»».
              searchValue={catSearch}
              onSearch={setCatSearch}
              filterOption={(input, option) =>
                String(option?.value).startsWith(NEW_PREFIX) || String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              disabled={!effectiveBlock}
              loading={catsQuery.isLoading}
              notFoundContent={catSearch.trim() ? null : 'Нет подразделов — введите название, чтобы создать'}
            />
          </div>
        </div>
        <div>
          <label className="fk-kb-label">Тип</label>
          <div className="fk-kb-chips">
            {TYPE_OPTIONS.map((t) => (
              <Chip key={t.value} active={type === t.value} onClick={() => setType(t.value)}>
                {t.label}
              </Chip>
            ))}
          </div>
        </div>
        <div>
          <label className="fk-kb-label">С чего начать</label>
          <div className="fk-kb-newart-start">
            {startCards.map((c) => (
              <button
                key={c.key}
                type="button"
                onClick={() => setStart(c.key)}
                aria-pressed={start === c.key}
                style={{
                  textAlign: 'left',
                  border: `2px solid ${start === c.key ? 'var(--fk-orange)' : 'var(--fk-border)'}`,
                  borderRadius: 'var(--fk-radius-md)',
                  padding: 12,
                  background: start === c.key ? 'var(--fk-orange-soft)' : 'var(--fk-surface)',
                  cursor: 'pointer',
                  color: 'var(--fk-text)',
                }}
              >
                <div style={{ fontWeight: 700, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                  {c.key === 'ai' && <ThunderboltOutlined style={{ color: 'var(--fk-orange)' }} />}
                  {c.title}
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--fk-text-muted)', marginTop: 2, lineHeight: 1.35 }}>{c.desc}</div>
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="fk-kb-label">Файл (PDF, DOCX) — приложим к статье</label>
          {file ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, border: '1px solid var(--fk-border)', borderRadius: 'var(--fk-radius-md)', padding: '8px 12px', background: 'var(--fk-surface)' }}>
              <PaperClipOutlined style={{ color: 'var(--fk-orange)' }} />
              <span style={{ fontWeight: 600, fontSize: 13, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={file.name}>{file.name}</span>
              <span style={{ fontSize: 12, color: 'var(--fk-text-muted)', whiteSpace: 'nowrap' }}>{fmtSize(file.size)}</span>
              <Button type="text" size="small" aria-label="Убрать файл" icon={<CloseOutlined />} disabled={createMut.isPending || fromVariant.isPending} onClick={() => changeFile(null)} style={{ marginLeft: 'auto' }} />
            </div>
          ) : (
            <Upload.Dragger
              accept=".pdf,.docx,.txt"
              maxCount={1}
              multiple={false}
              showUploadList={false}
              disabled={aiRunning}
              beforeUpload={(f) => {
                if (!FILE_EXT.test(f.name)) {
                  message.warning('Поддерживаются PDF, DOCX и TXT');
                  return Upload.LIST_IGNORE;
                }
                changeFile(f);
                return false;
              }}
              style={{ padding: '4px 0' }}
            >
              <p className="ant-upload-drag-icon" style={{ marginBottom: 4 }}><InboxOutlined /></p>
              <p className="ant-upload-text" style={{ fontSize: 14 }}>Перетащите файл сюда или нажмите, чтобы выбрать</p>
              <p className="ant-upload-hint" style={{ fontSize: 12 }}>
                {aiMode ? 'ИИ прочитает файл и коротко опишет его. Текст ниже — по желанию' : 'Файл появится во «Вложениях» статьи'}
              </p>
            </Upload.Dragger>
          )}
          {file && aiMode && !aiFile && (
            <Alert style={{ marginTop: 8 }} type="warning" showIcon message="Файл больше 30 МБ — ИИ его не прочитает. Он прикрепится к статье, а суть опишите текстом ниже." />
          )}
        </div>
        {aiMode && (
          <div style={{ display: 'grid', gap: 8 }}>
            <label className="fk-kb-label" htmlFor="ai-source-text">{aiFile ? 'Заметки к файлу (по желанию)' : 'Текст или тезисы — сколько угодно'}</label>
            <Input.TextArea
              id="ai-source-text"
              value={points}
              onChange={(e) => setPoints(e.target.value)}
              autoSize={{ minRows: 5, maxRows: 16 }}
              maxLength={60000}
              showCount
              disabled={aiRunning}
              placeholder={aiFile
                ? 'Что подчеркнуть в описании файла — по желанию.\nИИ прочитает документ сам и коротко изложит суть.'
                : 'Вставьте свой текст, заметки, регламент или список тезисов.\nИИ сохранит все факты, оформит в стиле базы знаний и предложит подраздел.'}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 12.5 }}>
              <span>Вариантов:</span>
              {[2, 3, 4].map((n) => (
                <Chip key={n} active={variantCount === n} onClick={() => { if (!aiRunning) setVariantCount(n); }}>{n}</Chip>
              ))}
              <span style={{ color: 'var(--fk-text-muted)' }}>ИИ не выдумывает: всё берёт из вашего текста{aiFile ? ' и файла' : ''}. Ничего не публикуется.</span>
            </div>
            {aiRunning && (
              <div style={{ display: 'grid', gap: 2, fontSize: 13, color: 'var(--fk-text-muted)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <Spin size="small" /> {variantsStatusText(aiProgress, aiElapsed)}
                </div>
                <div style={{ fontSize: 12, paddingLeft: 26 }}>Большой текст — до нескольких минут; готовые варианты появляются по одному. Окно не закрывайте.</div>
              </div>
            )}
            {aiRunning && aiNotice && <Alert type="info" showIcon message={aiNotice} />}
            {aiError && <Alert type="error" showIcon message={aiError} />}
            {aiNote && <Alert type="warning" showIcon message={aiNote} />}
            {(readyCount > 0 || pendingCount > 0) && (
              <div className="fk-kb-variants">
                {(variants ?? []).map((v, i) => {
                  const key = keys[i]!;
                  return (
                    <div key={key} className="fk-kb-variant">
                      <div style={{ fontWeight: 700, fontSize: 14 }}>{v.title}</div>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <Tag>{TYPE_LABEL[v.type] ?? v.type}</Tag>
                        {v.category && <Tag color={v.categoryIsNew ? 'orange' : undefined}>{v.categoryIsNew ? `новый подраздел «${v.category}»` : v.category}</Tag>}
                      </div>
                      {v.approach && <div style={{ fontSize: 12.5 }}>{v.approach}</div>}
                      {v.excerpt && <div style={{ fontSize: 12, color: 'var(--fk-text-muted)' }}>{v.excerpt}</div>}
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 'auto' }}>
                        <Button size="small" onClick={() => setPreviewKey(previewKey === key ? null : key)}>{previewKey === key ? 'Скрыть текст' : 'Показать текст'}</Button>
                        <Button size="small" type="primary" loading={fromVariant.isPending && !!fromVariant.variables && variantKey(fromVariant.variables) === variantKey(v)} disabled={fromVariant.isPending} onClick={() => fromVariant.mutate(v)}>Создать черновик</Button>
                      </div>
                    </div>
                  );
                })}
                {Array.from({ length: pendingCount }, (_, i) => (
                  <div key={`pending-${i}`} className="fk-kb-variant" data-pending="true" aria-busy="true">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, fontSize: 13.5, color: 'var(--fk-text-muted)' }}>
                      <Spin size="small" /> пишется…
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--fk-text-muted)' }}>Вариант появится здесь, как только ИИ допишет его</div>
                  </div>
                ))}
              </div>
            )}
            {previewVariant && (
              <div className="wiki-content fk-kb-variant-preview" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(markdownToHtml(previewVariant.bodyMd), { ADD_ATTR: ['data-callout', 'data-wikilink'] }) }} />
            )}
          </div>
        )}
        <div style={{ fontSize: 12, color: 'var(--fk-text-muted)' }}>
          Создадим черновик — текст, доступ и публикацию настроите в редакторе.
        </div>
      </div>
      <style>{`
        .fk-kb-newart-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 12px; }
        .fk-kb-newart-start { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
        @media (max-width: 560px) { .fk-kb-newart-start { grid-template-columns: 1fr; } }
        .fk-kb-variants { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 10px; }
        .fk-kb-variant { display: flex; flex-direction: column; gap: 6px; border: 1px solid var(--fk-border); border-radius: var(--fk-radius-md); padding: 12px; background: var(--fk-surface); }
        .fk-kb-variant[data-pending='true'] { border-style: dashed; justify-content: center; min-height: 120px; background: transparent; }
        .fk-kb-variant-preview { max-height: 420px; overflow: auto; border: 1px solid var(--fk-border); border-radius: var(--fk-radius-md); padding: 12px 16px; }
      `}</style>
    </Modal>
  );
}
